import { create } from 'zustand';
import { User, Task, Team, ContentPlan } from '../types';

interface AppState {
  user: User | null;
  tasks: Task[];
  teams: Team[];
  contentPlans: ContentPlan[];
  isDarkMode: boolean;
  setUser: (user: User | null) => void;
  setTasks: (tasks: Task[]) => void;
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  deleteTask: (taskId: string) => void;
  setTeams: (teams: Team[]) => void;
  addTeam: (team: Team) => void;
  updateTeam: (teamId: string, updates: Partial<Team>) => void;
  removeTeam: (teamId: string) => void;
  addTeamMember: (teamId: string, member: User) => void;
  removeTeamMember: (teamId: string, memberId: string) => void;
  setContentPlans: (plans: ContentPlan[]) => void;
  toggleDarkMode: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  user: null,
  tasks: [],
  teams: [],
  contentPlans: [],
  isDarkMode: false,
  setUser: (user) => set({ user }),
  setTasks: (tasks) => set({ tasks }),
  addTask: (task) => set((state) => ({ tasks: [...state.tasks, task] })),
  updateTask: (taskId, updates) =>
    set((state) => ({
      tasks: state.tasks.map((task) =>
        task.id === taskId ? { ...task, ...updates } : task
      ),
    })),
  deleteTask: (taskId) =>
    set((state) => ({
      tasks: state.tasks.filter((task) => task.id !== taskId),
    })),
  setTeams: (teams) => set({ teams }),
  addTeam: (team) => set((state) => ({ teams: [...state.teams, team] })),
  updateTeam: (teamId, updates) =>
    set((state) => ({
      teams: state.teams.map((t) => (t.id === teamId ? { ...t, ...updates } : t)),
    })),
  removeTeam: (teamId) =>
    set((state) => ({ teams: state.teams.filter((t) => t.id !== teamId) })),
  addTeamMember: (teamId, member) =>
    set((state) => ({
      teams: state.teams.map((t) =>
        t.id === teamId ? { ...t, members: [...(t.members || []), member.id] } : t
      ),
    })),
  removeTeamMember: (teamId, memberId) =>
    set((state) => ({
      teams: state.teams.map((t) =>
        t.id === teamId
          ? { ...t, members: (t.members || []).filter((id) => id !== memberId) }
          : t
      ),
    })),
  setContentPlans: (plans) => set({ contentPlans: plans }),
  toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),
}));